import { Factory } from "@/data/dao/Factory";
import { OrderDao } from "@/data/dao/service/OrderDao";
import { PaymentState, ReceiptType } from "@/data/model/Enums";
import type { ITransaction, IVatGroupDto } from "@/data/model/Order";



import { BaseModal } from "@/shared/elements/BaseModal";
import { translations } from "@/shared/localization/translations";
import { PaymentMethodUtil } from "@/shared/utils/PaymentMethodUtil";
import { toMoney } from "@/shared/utils/format";



import { PaymentInfoHeader } from "@/operator/elements/transaction/PaymentInfoHeader";
import { PaymentItemsList } from "@/operator/elements/transaction/PaymentItemList";
import { type OpenBillTableSelectedEventData, operatorEvents } from "@/operator/events/operatorEvents";
import { trans } from "@/operator/localization/Trans";



import { PrintOrderReceiptWindow } from "./PrintOrderReceiptWindow";
import { SendOrderReceiptWindow } from "./SendOrderReceiptWindow";


export class TransactionDetailsWindow extends BaseModal {
    private $head!: JQuery;
    private $itemList!: JQuery;
    private $totals!: JQuery;
    private $receiptDropDown?: JQuery;

    private readonly orderDao: OrderDao;
    private readonly transactionId: string;

    private isInitialized?: boolean;

    constructor(transactionId: string) {
        super(undefined, undefined, false, true);

        this.transactionId = transactionId;

        this.orderDao = Factory.getInstance<OrderDao>(OrderDao);

        this.$element.addClass("transaction-details");

        this.loadTransaction(transactionId);
    }

    private async loadTransaction(id: string) {
        const transaction = await this.orderDao.getTransaction(id);
        this.updateModalContent(transaction);
    }

    private updateModalContent(transaction: ITransaction) {
        const tip = transaction.tip ?? 0;

        // if it's the first time - initialize containers
        if (!this.isInitialized) {
            this.$body.addClass("scrollable");
            this.$head = $('<div class="head" />').appendTo(this.$header);
            this.$itemList = $('<div class="itemlist" />').appendTo(this.$body);
            this.$totals = $('<div class="totals" />').appendTo(this.$footer!);
            this.isInitialized = true;
        }

        if (this.$receiptDropDown != null) {
            this.$receiptDropDown.remove();
            this.$receiptDropDown = undefined;
        }

        // cleanup previous data
        this.$head.empty();
        this.$itemList.empty();
        this.$totals.empty();

        // set header info
        PaymentInfoHeader.render(this.$head, transaction);

        // set payment items list
        PaymentItemsList.render(this.$itemList, transaction.paymentItems, true);

        // display discount
        if (transaction.orderLevelDiscountAmount > 0) {
            const $discount = $(
                `<div class="totals-item"><div> ${trans.orderDetails.discount} </div><div style="color: #fd397a">${toMoney(-1 * transaction.orderLevelDiscountAmount, true)}</div></div>`
            );
            this.$totals.append($discount);
        }

        // set vat groups in summary
        for (const vatGroup of transaction.vatGroups) {
            this.$totals.append(
                `<div class="totals-item"><div>${this.getVatGroupTitle(
                    vatGroup
                )}</div><div>${toMoney(vatGroup.totalAmount, true)}</div></div>`
            );
        }

        // display delivery fee
        const $deliveryFee = $(
            `<div class="totals-item"><div> ${translations.pay.fees} </div><div>${toMoney(transaction.deliveryFee, true)}</div></div>`
        );

        this.$totals.append($deliveryFee);

        // display tip
        if (tip > 0) {
            const $tip = $(
                `<div class="totals-item"><div> ${trans.orderDetails.tip} </div><div>${toMoney(tip, true)}</div></div>`
            );
            if (transaction.cancelledOrdersTip > 0) {
                $tip.addClass("cancelled");
            }
            this.$totals.append($tip);
        }

        const paymentMethodDivs = PaymentMethodUtil.generatePaymentMethodSplitDivs("", transaction.paymentMethodSplits);

        // display summary
        $(
            `<div class='total totals-item'>
                <div style="display: flex;">
                    <div style="margin-right: 8px;">${trans.payments.total}</div>
                    ${paymentMethodDivs.join(" ")} 
                </div>
                <div>${toMoney(transaction.totalPaid, true)}</div>
            </div>`
        ).appendTo(this.$totals);

        if (transaction.refundedAmount != 0) {
            $(
                `<div class='totals-item'>
                <div style="display: flex; ">
                    <div style="margin-right: 8px;">${trans.payments.refunded}</div>
                </div>
                <div class="refunded">${toMoney(transaction.refundedAmount * -1, true)}</div>
            </div>`
            ).appendTo(this.$totals);
        }

        if (transaction.state === PaymentState.Confirmed) {
            $(
                '<div id="receipt-section"><button class="btn btn-success ml-2 dropdown-toggle" type="button" id="receiptbtn" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">' +
                    trans.orderDetails.receipt +
                    "</button>" +
                    '<div class="dropdown-menu" aria-labelledby="receiptbtn"><a class="dropdown-item" href="#" data-action="sendreceipt">' +
                    trans.orderDetails.sendReceipt +
                    '</a><a class="dropdown-item" href="#" data-action="printreceipt">' +
                    trans.orderDetails.printReceipt +
                    "</a></div></div>"
            ).appendTo(this.$footer!);
        }

        this.$footer!.find("#receipt-section > .dropdown-menu > .dropdown-item").click((e) => {
            e.preventDefault();
            switch ($(e.currentTarget).attr("data-action")) {
                case "sendreceipt":
                    this.openSendReceipt();
                    this.close();
                    break;
                case "printreceipt":
                    this.showPrintOrderReceipt();
                    this.close();
                    break;
            }
        });
    }

    private showPrintOrderReceipt() {
        const m = new PrintOrderReceiptWindow(this.transactionId, ReceiptType.Payment);
        m.show();
    }

    public openSendReceipt() {
        const m = new SendOrderReceiptWindow(this.transactionId, ReceiptType.Payment);
        m.show();
    }

    private getVatGroupTitle(vatGroup: IVatGroupDto): string {
        return trans.orderDetails.consumptionInclVat.replace("{0}", this.getVatPercentValueAsString(vatGroup.vatValue));
    }

    private getVatPercentValueAsString(vat: number): string {
        return (Math.round(vat * 100000) / 1000).toString();
    }

    protected onShow() {
        operatorEvents.transactionDetailsWindow.openBillTableSelected.add(this.onOpenBillTableSelected, this);
        operatorEvents.modals.closeAllModals.add(this.close, this);
    }

    protected onHide() {
        operatorEvents.transactionDetailsWindow.openBillTableSelected.remove(this.onOpenBillTableSelected, this);
        operatorEvents.modals.closeAllModals.remove(this.close, this);
        this.$element.remove();
    }

    private onOpenBillTableSelected(eventData: OpenBillTableSelectedEventData) {
        operatorEvents.modals.closeAllModals.dispatch();
        operatorEvents.showOrderAndPayView.dispatch({
            tableId: eventData.tableId,
            billId: eventData.billId,
        });
    }
}
